import React from "react";
import { FilterSessionsInputs } from "../../types";
import { ActiveSearchFilter, SearchDropdownForm, SearchItemConfig } from "src/components/Form/search-dropdown-form";

export type SessionsSearchRouteState = {
  id?: string;
  coach?: string;
  learner?: string;
  state?: FilterSessionsInputs["state"];
  type?: FilterSessionsInputs["type"];
  currency?: string;
  space?: string;
};

type Props = {
  updateFilterQuery: (filter: SessionsSearchRouteState) => void;
  initialQuery?: SessionsSearchRouteState;
};

type SearchKey = "id" | "coach" | "learner" | "state" | "space" | "type";

const searchItems: Record<SearchKey, SearchItemConfig> = {
  id: {
    type: "input",
    placeholder: "Enter a session ID",
  },
  coach: {
    type: "input",
    placeholder: "Enter a coach ID, email address, or phone number",
  },
  learner: {
    type: "input",
    placeholder: "Enter a LearnerId, email address, or phone number",
  },
  state: {
    type: "select",
    placeholder: "All Status",
    options: [
      { value: "STANDBY", text: "Standby" },
      { value: "ACTIVE", text: "Active" },
      { value: "MISSED", text: "Missed" },
      { value: "REJECTED", text: "Rejected" },
      { value: "COMPLETED", text: "Completed" },
      { value: "CANCELLED", text: "Cancelled" },
    ],
  },
  space: {
    type: "input",
    placeholder: "Enter a language code, e.g., en or nl",
  },
  type: {
    type: "select",
    placeholder: "All Types",
    options: [
      { value: "BOOKING", text: "Booking" },
      { value: "DIRECT_CALL", text: "Direct Call" },
      { value: "LOUNGE", text: "Lounge" },
    ],
  },
};

const labelMap: Record<keyof typeof searchItems, string> = {
  id: "Session ID",
  coach: "Coach",
  learner: "Learner",
  state: "State",
  space: "Language",
  type: "Type",
};

export const SessionsSearch: React.FC<Props> = ({ updateFilterQuery, initialQuery = {} }) => {
  const handleSubmit = (key: SearchKey, value: string) => {
    const nextFilters: SessionsSearchRouteState = { ...initialQuery };

    if (!value) {
      delete nextFilters[key];
      updateFilterQuery(nextFilters);
      return;
    }

    switch (key) {
      case "state":
        nextFilters.state = value as FilterSessionsInputs["state"];
        break;
      case "type":
        nextFilters.type = value as FilterSessionsInputs["type"];
        break;
      case "id":
        nextFilters.id = value;
        break;
      case "coach":
        nextFilters.coach = value;
        break;
      case "learner":
        nextFilters.learner = value;
        break;
      case "space":
        nextFilters.space = value;
        break;
    }

    updateFilterQuery(nextFilters);
  };

  const activeFilters = Object.entries(initialQuery).reduce<ActiveSearchFilter<SearchKey>[]>((filters, [key, value]) => {
    if (!value || !(key in searchItems)) return filters;

    const searchKey = key as keyof typeof searchItems;
    const config = searchItems[searchKey];
    const rawValue = String(value);
    const resolvedValue =
      config.type === "select" ? config.options?.find((option) => option.value === rawValue)?.text || rawValue : rawValue;

    filters.push({
      key: searchKey,
      label: labelMap[searchKey],
      value: resolvedValue,
    });

    return filters;
  }, []);

  return (
    <div className="pt-5 pb-7">
      <SearchDropdownForm
        searchItems={searchItems}
        handleSubmit={handleSubmit}
        activeFilters={activeFilters}
        onRemoveFilter={(key) => handleSubmit(key, "")}
        onClearAll={() => updateFilterQuery({})}
      />
    </div>
  );
};
